import type { ReactNode } from "react";
import { SkillProgress } from "../../../shared/components";
import type {
  Insight,
  ParentActivity,
  ParentDashboard,
  ProgressSnapshot,
} from "../../../shared/api/parent";
import type { ChildLearning } from "../../../shared/api/answers";
import { reportLearning } from "./learningModel";
import {
  accuracy,
  consistency,
  series,
  skillShares,
  topGame,
} from "./activityModel";
import { gameArtFor, skillTint } from "./Charts";
import { skillLabel } from "./DayViews";
import { skillSections } from "./skillSections";
import { stickerBook } from "./stickerBook";
import { PARENT_COPY, type Lang } from "./copy";

/**
 * The document a parent prints.
 *
 * 🔴 THE BUTTON WAS ALWAYS `window.print()` AND WHAT IT PRINTED WAS AN APP. C2's
 * header has carried "Print or save as PDF" since the tier shipped, and OD-59's
 * refusal of a server-side renderer was right for all three of its stated
 * reasons. What nobody built was the thing to print: the shell is
 * `h-dvh overflow-hidden` with the page inside a scroller, so the browser was
 * handed one viewport and produced a single sheet with a sidebar down the left
 * and the rest of the child's progress clipped off the bottom. Nobody sends that
 * to a grandparent.
 *
 * 🔴 SO THIS IS A SHEET, NOT THE SCREEN WITH ITS BUTTONS HIDDEN. It is `hidden`
 * until `@media print`, it reads top to bottom in one column, and it repeats
 * nothing the screen shows in a layout the screen chose. `print.css` unwinds the
 * scroll containers and freezes the entrance animations — a `play-rise` element
 * caught mid-delay prints at its FROM state, which on a level bar is 15% of the
 * truth.
 *
 * 🔴 IT IS GENEROUS AND IT IS NOT FLATTERING, AND THE DIFFERENCE IS THE WHOLE
 * DESIGN. A parent should want to show this to somebody — that is a real
 * requirement and it is why the headline numbers are the child's own bests and
 * the layout is a certificate rather than a spreadsheet. But every number on it
 * is a count of something that happened, with its denominator beside it, and
 * there is no grade, no rank, no target and no other child anywhere on the page.
 * The honest version is already something to be proud of; an invented ranking
 * would be the one thing that makes it untrue — and it is the parent who would
 * repeat it to the school.
 *
 * ⚠️ WHAT IS DELIBERATELY ABSENT: attendance. It is the school's record, it
 * lives on C6 with the sentence that explains what an empty square means, and a
 * printed "3 days absent" travelling out of that context is exactly the number a
 * family gets asked about. The report is about what the child DID.
 */

export interface ReportSheetProps {
  lang: Lang;
  dash: ParentDashboard;
  /** Null while the read is in flight, or when it failed — the sheet degrades. */
  activity: ParentActivity | null;
  progress: ProgressSnapshot | null;
  insight: Insight | null;
  /**
   * The per-question summary (`/learning/players/:id/summary`) — the five areas
   * and how the child answered. Optional: C2's older screen does not read it,
   * and a sheet without it prints exactly as it did.
   */
  learning?: ChildLearning | null;
  /** When the sheet was drawn. Passed in, never read from a clock in render. */
  now: Date;
}

export function ReportSheet({
  lang,
  dash,
  activity,
  progress,
  insight,
  learning = null,
  now,
}: ReportSheetProps) {
  const t = PARENT_COPY[lang];
  const locale = "en-IN";
  const sections = skillSections(dash, lang);
  const answered = reportLearning(learning);

  const days = activity?.days ?? [];
  const totals = activity?.totals ?? null;
  const right = totals ? accuracy(totals) : null;
  const played = activity ? consistency(activity) : null;
  const bars = series(days, (d) => d.events);
  const shares = skillShares(days);
  const best = topGame(days);
  const bestArt = best ? gameArtFor(best.game) : null;
  const stickers = progress ? stickerBook(progress.stickers) : [];

  const period =
    days.length > 0
      ? t.reportPeriod(
          formatDate(days[0].day, locale),
          formatDate(days[days.length - 1].day, locale),
        )
      : "";

  return (
    /*
     * ⚠️ `hidden print:block` — THE SHEET DOES NOT EXIST ON SCREEN. It is in the
     * DOM rather than built on demand because `window.print()` is synchronous:
     * there is no moment between the click and the dialogue in which to fetch
     * anything, so everything the document needs must already be loaded. That is
     * also why every field above is nullable — the sheet prints what arrived and
     * says nothing about what did not.
     */
    <div className="print-sheet hidden font-dash text-ink print:block">
      {/* ───────────────────────────────────────────────── the masthead ── */}
      <header className="print-keep border-b-2 border-line pb-4">
        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0">
            {/* §10 — the wordmark is artwork and is never re-typeset. */}
            <img src="/kalqy-wordmark.png" alt="Kalqy" className="h-6 w-auto" />
            <p className="mt-3 font-dash text-[11px] font-bold tracking-[0.14em] text-muted uppercase">
              {t.reportTitle}
            </p>
            {/* ⚠️ THE CHILD'S NAME IS THE TITLE, IN BALOO, AND IT IS THE ONE
                STRING ON THIS DOCUMENT SET IN THE CHILD'S TYPEFACE. §4 splits the
                two families and `ChildHero` takes the same single exception for
                the same reason: the document is about them. */}
            <h1 className="font-kid text-[34px] leading-none font-extrabold text-ink">
              {dash.childName}
            </h1>
            <p className="mt-1.5 text-[12px] text-ink-soft">
              {dash.className
                ? `${t.reportClass}: ${dash.className}`
                : t.reportSubtitle}
            </p>
          </div>

          <div className="shrink-0 text-right text-[11px] text-muted">
            {period ? (
              <p className="tabular font-bold text-ink-soft">{period}</p>
            ) : null}
            <p className="tabular mt-1">
              {t.reportGenerated(
                now.toLocaleDateString(locale, { timeZone: "UTC" }),
              )}
            </p>
          </div>
        </div>
      </header>

      {/* ───────────────────────────────────────────────── the headlines ── */}
      <Section title={t.reportHighlights}>
        {totals === null ? (
          <p className="text-[12px] text-muted">{t.reportNoData}</p>
        ) : (
          <div className="grid grid-cols-4 gap-3">
            <Headline
              label={t.metricDaysPlayed}
              value={
                played ? `${String(played.value)}/${String(played.of)}` : "—"
              }
              detail={
                played
                  ? t.metricDaysPlayedOf(played.value, played.of)
                  : undefined
              }
              tint="var(--color-leaf)"
            />
            <Headline
              label={t.metricAccuracy}
              value={right ? `${String(right.value)}/${String(right.of)}` : "—"}
              detail={
                right
                  ? t.metricAccuracyOf(right.value, right.of)
                  : t.metricAccuracyNone
              }
              tint="var(--color-sky)"
            />
            <Headline
              label={t.metricLevels}
              value={totals.levelUps}
              detail={t.metricLevelsHint}
              tint="var(--color-primary)"
            />
            <Headline
              label={t.metricBestRun}
              value={totals.bestRun}
              detail={t.metricBestRunValue(totals.bestRun)}
              tint="var(--color-grape)"
            />
          </div>
        )}

        {/*
          ⚠️ THE SENTENCE UNDER THE NUMBERS IS THE ONE A PARENT READS ALOUD. "Their
          favourite this month was Sky Quest" is what makes this a report about a
          child rather than a table about a user, and it is composed entirely from
          counts — no adjective the data does not support.
        */}
        {best && bestArt ? (
          <p className="mt-3 text-[12px] text-ink-soft">
            <span className="font-bold text-ink">{bestArt.title[lang]}</span>
            {" · "}
            {t.chartSkillsOf(best.events, totals?.events ?? best.events)}
          </p>
        ) : null}
      </Section>

      {/* ─────────────────────────────────────────── what they are learning ── */}
      <Section title={t.reportLearning}>
        <div className="grid grid-cols-2 gap-x-8 gap-y-4">
          {sections.content.map((s) => (
            <div key={s.skill} className="print-keep">
              {/* 🔴 `SkillProgress` DRAWS IT, HERE AS ON SCREEN. It has no
                  `variant` prop and derives the family from the skill, so a
                  content skill cannot print as a percentage even by accident —
                  the guarantee is worth more on the page that leaves the
                  building than on the one that does not. */}
              <SkillProgress
                skill={s.skill}
                label={s.label}
                value={s.level}
                levelTemplate={t.levelTemplate}
              />
            </div>
          ))}
        </div>
      </Section>

      {/* ──────────────────────────────────────── every question, by area ── */}
      {/*
        🔴 ALL FIVE AREAS, EVERY TIME, AND COUNTS ONLY. An area the child has not
        reached says so rather than dropping off the page; every figure carries
        its denominator; and there are no times here — the footer promises no
        estimates, and a usual time is one (it stays on the screen, where it says
        "about"). A wrong answer is "still learning"; a Turo pose or a question
        left unanswered is never counted as one.
      */}
      {answered ? (
        <Section title={t.reportAreas}>
          <table className="w-full border-collapse text-[11.5px]">
            <thead>
              <tr className="text-left text-[10px] font-bold tracking-[0.08em] text-muted uppercase">
                <th className="pb-1.5 font-bold" scope="col">
                  <span className="sr-only">Area</span>
                </th>
                <th className="tabular pb-1.5 text-right font-bold" scope="col">
                  {t.reportAreaQuestions}
                </th>
                <th className="tabular pb-1.5 text-right font-bold" scope="col">
                  {t.reportAreaFirstTry}
                </th>
                <th className="tabular pb-1.5 text-right font-bold" scope="col">
                  {t.reportAreaStillLearning}
                </th>
              </tr>
            </thead>
            <tbody>
              {answered.areas.map((a) => (
                <tr key={a.game} className="print-keep border-t border-line align-top">
                  <th scope="row" className="py-1.5 pr-3 text-left font-normal">
                    <span className="font-bold text-ink">{a.headline}</span>
                    <span className="text-muted"> · {a.title}</span>
                    {a.posesLine ? (
                      <span className="block text-[10.5px] text-muted">{t.reportPoses(a.posesLine)}</span>
                    ) : null}
                  </th>
                  {a.played ? (
                    <>
                      <td className="tabular py-1.5 text-right text-ink">{a.answered}</td>
                      <td className="tabular py-1.5 text-right text-ink">{a.firstTryLine ?? "—"}</td>
                      <td className="tabular py-1.5 text-right text-ink">{a.answers > 0 ? a.stillLearning : "—"}</td>
                    </>
                  ) : (
                    <td colSpan={3} className="py-1.5 text-right text-muted">
                      {t.reportAreaNotPlayed}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="tabular mt-2 text-[11px] text-muted">{t.reportAreasTotal(answered.answered)}</p>
        </Section>
      ) : null}

      {/* ────────────────────────────────────────────── how they are moving ── */}
      <Section title={t.reportMoving}>
        <div className="flex flex-col gap-3">
          {sections.motor.map((s) => (
            <div key={s.skill} className="print-keep">
              <SkillProgress
                skill={s.skill}
                label={s.label}
                value={s.percent}
              />
              {/* 🔴 CONTRACT D4 — THE DENOMINATOR TRAVELS WITH THE BAR, and on a
                  printed sheet it is the only place a reader can check it. */}
              <p className="mt-0.5 text-[11px] text-muted">{s.caption}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ─────────────────────────────────── the movement that answered ── */}
      {answered && (answered.movement.sentence || answered.movement.moved) ? (
        <Section title={t.reportAnswering}>
          <div className="print-keep">
            {answered.movement.sentence ? (
              <p className="text-[12px] font-bold text-ink">{answered.movement.sentence}</p>
            ) : null}
            {answered.movement.moved ? (
              <p className="tabular mt-0.5 text-[11.5px] text-ink-soft">{answered.movement.moved}</p>
            ) : null}
            {answered.movement.rows.length > 0 ? (
              <ul className="mt-2 grid grid-cols-3 gap-x-8 gap-y-1">
                {answered.movement.rows.map((r) => (
                  <li key={r.movement} className="flex items-center justify-between gap-3 text-[11.5px]">
                    <span className="text-ink">{r.label}</span>
                    <span className="tabular text-muted">{r.count}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </Section>
      ) : null}

      {/* ─────────────────────────────────────────────────────── their days ── */}
      {days.length > 0 && (
        <Section title={t.reportDays}>
          <div className="print-keep">
            {/*
              The same bars the screen draws, without the tap. A printed chart
              cannot be hovered, so every column carries its own date underneath
              at the week boundaries and the tallest value is stated in words.
            */}
            <div className="flex h-20 items-end gap-[2px]">
              {bars.bars.map((bar) => (
                <span
                  key={bar.day}
                  className="flex min-w-0 flex-1 items-end rounded-[2px]"
                  style={{
                    background:
                      "color-mix(in srgb, var(--color-line) 45%, transparent)",
                  }}
                >
                  <span
                    className="block w-full rounded-[2px]"
                    style={{
                      height: `${String(bar.value === 0 ? 0 : Math.max(6, Math.round(bar.ratio * 100)))}%`,
                      background: "var(--color-sky)",
                    }}
                  />
                </span>
              ))}
            </div>
            <p className="tabular mt-1.5 text-[11px] text-muted">
              {t.chartActivityMax(bars.max)}
              {totals && totals.minutes > 0
                ? ` · ${t.metricMinutes}: ${t.metricMinutesValue(totals.minutes)}`
                : ""}
            </p>
          </div>

          {shares.length > 0 && (
            <ul className="mt-4 grid grid-cols-2 gap-x-8 gap-y-1.5">
              {shares.map((row) => (
                <li
                  key={row.skill}
                  className="flex items-center justify-between gap-3 text-[12px]"
                >
                  <span className="flex min-w-0 items-center gap-2 text-ink">
                    <span
                      aria-hidden="true"
                      className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ background: skillTint(row.skill) }}
                    />
                    {skillLabel(row.skill, lang)}
                  </span>
                  <span className="tabular shrink-0 text-muted">
                    {row.events}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      )}

      {/* ──────────────────────────────────────────── what they have collected ── */}
      <Section title={t.reportRewards}>
        <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-[12px]">
          <span className="flex items-baseline gap-2">
            <span className="tabular text-[22px] leading-none font-extrabold text-ink">
              {progress?.coins ?? dash.coins}
            </span>
            <span className="text-muted">{t.coins}</span>
          </span>
          <span className="flex items-baseline gap-2">
            <span className="tabular text-[22px] leading-none font-extrabold text-ink">
              {progress?.stickers.length ?? dash.stickers}
            </span>
            <span className="text-muted">{t.stickersEarned}</span>
          </span>
          <span className="flex items-baseline gap-2">
            <span className="tabular text-[22px] leading-none font-extrabold text-ink">
              {dash.streakDays}
            </span>
            <span className="text-muted">{t.streak}</span>
          </span>
        </div>

        {stickers.length > 0 && (
          /* 🔴 THE STICKERS, NAMED. This is the line that would otherwise print
             `sky-quest-l2` on a document a family keeps. */
          <ul className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-[11px] text-ink-soft">
            {stickers.map((group) => (
              <li key={group.game?.id ?? "unknown"}>
                {group.game
                  ? t.stickerCountFor(
                      group.stickers.length,
                      group.game.title[lang],
                    )
                  : t.stickerUnknown}
              </li>
            ))}
          </ul>
        )}
      </Section>

      {insight ? (
        <Section title={t.reportThisWeek}>
          <p className="max-w-[80ch] text-[12px] leading-relaxed text-ink">
            {insight.summary}
          </p>
        </Section>
      ) : null}

      {/*
        🔴 THE FOOTER IS A CLAIM ABOUT THE DOCUMENT, AND IT IS THE HONEST ONE. It
        says every number is a count of real activity and that there are no
        grades and no comparisons — which is what makes the page safe to hand to
        a school. A report that did not say this would be one whose reader had to
        assume it.
      */}
      <footer className="mt-6 border-t border-line pt-3 text-[10px] leading-relaxed text-muted">
        <p className="max-w-[95ch]">{t.reportFooter}</p>
      </footer>
    </div>
  );
}

/** One block of the sheet. `print-keep` so a heading never orphans onto a page. */
function Section({
  title,
  children,
}: {
  title: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="mt-5 print-keep">
      <h2 className="mb-2.5 border-b border-line pb-1 font-dash text-[11px] font-bold tracking-[0.14em] text-muted uppercase">
        {title}
      </h2>
      {children}
    </section>
  );
}

/** A headline number, with the division underneath it. */
function Headline({
  label,
  value,
  detail,
  tint,
}: {
  label: ReactNode;
  value: ReactNode;
  detail?: ReactNode;
  tint: string;
}) {
  return (
    <div
      className="rounded-lg border p-3"
      style={{
        borderColor: `color-mix(in srgb, ${tint} 35%, transparent)`,
        background: `color-mix(in srgb, ${tint} 8%, transparent)`,
      }}
    >
      <p className="text-[10px] font-bold tracking-[0.08em] text-muted uppercase">
        {label}
      </p>
      <p className="tabular mt-1.5 text-[26px] leading-none font-extrabold text-ink">
        {value}
      </p>
      {detail ? (
        <p className="mt-1 text-[10px] leading-snug text-muted">{detail}</p>
      ) : null}
    </div>
  );
}

/**
 * ⚠️ FORMATTED IN UTC. The day key is an IST calendar day written `YYYY-MM-DD`;
 * letting the browser apply its own offset would date the report a day early for
 * any reader west of Greenwich.
 */
function formatDate(key: string, locale: string): string {
  return new Date(`${key}T00:00:00.000Z`).toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}
